<template>
  <div>
    <v-row>
      <v-col>
        <PInputField
          :id="'add-supplier__company-name'"
          :value="editableSupplier.companyName"
          :label="'Company Name'"
          :error="v$.companyName.$error"
          :error-messages="v$.companyName.$errors[0]?.$message"
          @update-event="(val) => (editableSupplier.companyName = val)"
        />
      </v-col>
      <v-col>
        <PInputField
          :id="'add-supplier__contact-email'"
          :value="editableSupplier.email"
          :label="'Contact Email'"
          :error="v$.email.$error"
          :error-messages="v$.email.$errors[0]?.$message"
          @update-event="(val) => (editableSupplier.email = val)"
        />
      </v-col>
    </v-row>
    <v-row v-if="canUploadBusinessPartner">
      <v-col cols="6">
        Business relation
        <PAutocomplete
          :items="['Supplier', 'Business partner']"
          @field-updated="updateBusinessRelationship"
        />
      </v-col>
    </v-row>
    <v-row class="text-right">
      <v-col class="pb-0">
        <PPrimaryButton
          id="add-supplier__action"
          :loading="suppliersStore.loading"
          @click="post"
          >{{ actionButtonText }}
        </PPrimaryButton>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import useVuelidate from "@vuelidate/core";
import { email, required } from "@vuelidate/validators";
import { Supplier } from "@/models/supplier";
import { computed, ref, onBeforeUpdate } from "vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import PInputField from "@/components/custom/PInputField.vue";
import { useSuppliersStore } from "@/stores/suppliersStore";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";

const emit = defineEmits<{
  (e: "change"): void;
}>();

const props = withDefaults(
  defineProps<{
    canUploadBusinessPartner: boolean;
    editMode?: boolean;
    supplier?: Supplier;
  }>(),
  {
    editMode: false,
  }
);

const snackBarStore = useSnackBarStore();
const suppliersStore = useSuppliersStore();

const editableSupplier = ref(new Supplier());

onBeforeUpdate(() => {
  if (props.supplier == null) {
    editableSupplier.value = new Supplier();
  } else {
    editableSupplier.value = { ...props.supplier };
  }
});

const rules = computed(() => ({
  companyName: { required },
  email: { required, email },
}));

const v$ = useVuelidate<Supplier>(rules, editableSupplier);
const actionButtonText = computed(() => (props.editMode ? "Update" : "Add"));

async function post(): Promise<void> {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }

  try {
    await suppliersStore.addSupplierToList(editableSupplier.value);
    snackBarStore.showSuccess();
    reset();
    emit("change");
  } catch (error: any) {
    snackBarStore.showError(error.message);
  }
}

function updateBusinessRelationship(businessRelation: string): void {
  editableSupplier.value.isBusinessPartner =
    businessRelation == "Business partner";
}

const reset = () => {
  v$.value.$reset();
  editableSupplier.value = new Supplier();
};
</script>
